/**
 * Piezas visuales del módulo Navigation — spec "Patient Navigation Pre-Call
 * Screen" (2026-08-19). Tokens: navy #122b44 (tinta + primario), teal #228a8d
 * (interactivo/kickers), sage (positivo), amber (atraso/aviso). Montserrat
 * para títulos/labels/números, Manrope para cuerpo. Los colores viven en
 * tailwind.config bajo el prefijo `nav`; acá viven las CLASES compuestas para
 * que cada pantalla no reinvente el botón.
 *
 * Regla del spec §10: guardar directo lo reversible, capa para lo que pide
 * datos, confirmación solo para lo irreversible, página nueva para nada.
 */
import { type ReactNode } from 'react';
import { ModalShell } from '@/components/ModalShell';
import { cn } from '@/lib/utils';
import { useBodyScrollLock } from '@/lib/useBodyScrollLock';
import { useEffect } from 'react';

/** Botón primario: navy sólido. */
export const BTN_PRI =
  'inline-flex items-center justify-center gap-2 rounded-lg border-[1.5px] border-transparent '
  + 'bg-nav-navy text-white font-head font-semibold text-sm px-4 py-2 leading-tight cursor-pointer '
  + 'hover:bg-nav-navyHover disabled:opacity-45 disabled:cursor-not-allowed transition-colors';

/** Botón secundario: blanco con borde; el hover lo vuelve teal. */
export const BTN_SEC =
  'inline-flex items-center justify-center gap-2 rounded-lg border-[1.5px] border-nav-line '
  + 'bg-white text-nav-navy font-head font-semibold text-sm px-4 py-2 leading-tight cursor-pointer '
  + 'hover:border-nav-teal hover:text-nav-tealDark disabled:opacity-45 disabled:cursor-not-allowed transition-colors';

/** Botón fantasma: texto teal, sin borde. */
export const BTN_GHO =
  'inline-flex items-center gap-1 bg-transparent border-0 text-nav-teal font-head font-semibold '
  + 'text-[13px] px-0 py-1 cursor-pointer hover:text-nav-tealDark disabled:opacity-45 transition-colors';

/** Variante compacta de los botones de fila (§01/§05). */
export const BTN_SM = 'text-[13px] px-3 py-[7px]';

/** Input del spec: borde 1.5 line, radio 8, focus teal. */
export const INP =
  'w-full box-border font-body text-sm text-nav-navy bg-white border-[1.5px] border-nav-line '
  + 'rounded-lg px-3 py-2 focus:outline focus:outline-2 focus:outline-nav-teal focus:outline-offset-1 '
  + 'focus:border-nav-teal placeholder:text-nav-faint';

/** Label chico uppercase de formularios de capa. */
export const FIELD_LABEL =
  'block font-head text-[11px] font-bold tracking-[.08em] uppercase text-nav-slate mb-1.5';

/** Kicker de sección: Montserrat 700 10px uppercase teal. */
export function Kicker({ children, tone = 'teal', className = '' }: {
  children: ReactNode;
  tone?: 'teal' | 'amber';
  className?: string;
}): JSX.Element {
  const color = tone === 'amber' ? 'text-nav-amberText' : 'text-nav-teal';
  return (
    <p className={`font-head font-bold text-[10px] tracking-[.14em] uppercase ${color} mb-3 ${className}`}>
      {children}
    </p>
  );
}

/** Pill uppercase 10px del spec (§01). */
export function NavPill({ tone, children, className = '' }: {
  tone: 'warn' | 'ok' | 'teal' | 'grey';
  children: ReactNode;
  className?: string;
}): JSX.Element {
  const tones: Record<string, string> = {
    warn: 'bg-nav-amberTint text-nav-amberText',
    ok: 'bg-nav-sageTint text-nav-sageText',
    teal: 'bg-nav-tealTint text-nav-tealDark',
    grey: 'bg-nav-hair text-nav-slate',
  };
  return (
    <span
      className={`font-head font-bold text-[10px] tracking-[.06em] uppercase px-2.5 py-1 rounded-full inline-block ${tones[tone]} ${className}`}
    >
      {children}
    </span>
  );
}

/**
 * La pantalla del módulo: tarjeta blanca, radio 14, columna de ~720-760px —
 * el navigator trabaja a media pantalla, con findhelp al lado.
 */
export function Scr({ children, className = '' }: { children: ReactNode; className?: string }): JSX.Element {
  return (
    <div
      className={`bg-white text-nav-navy font-body text-[15px] leading-relaxed rounded-[14px] shadow-[0_1px_2px_rgba(18,43,68,.06),0_8px_24px_rgba(18,43,68,.10)] px-6 sm:px-[30px] pt-[26px] pb-8 ${className}`}
    >
      {children}
    </div>
  );
}

/** Encabezado de pantalla del módulo: marca + «· Navigation» + quién. */
export function ScrBrandRow({ right, crumb, className = '' }: {
  right?: ReactNode;
  /** §06: «· Navigation · EA de Yolanda Brito» — el tramo después de Navigation. */
  crumb?: string;
  className?: string;
}): JSX.Element {
  return (
    <div className={`flex items-center gap-2.5 pb-3 mb-5 border-b border-nav-hair ${className}`}>
      {/* El mark de la marca (kit del CEO, Recurso 4): ya viene con
          transparencia, asi que no necesita recorte redondo. */}
      <img src="/mark.png" alt="Pathway" className="h-[26px] w-auto" />
      <span className="font-head text-xs font-extrabold tracking-[.14em] text-nav-navy">PATHWAY</span>
      <span className="text-xs text-nav-slate">· Navigation{crumb ? ` · ${crumb}` : ''}</span>
      {right !== undefined && <span className="ml-auto text-xs text-nav-slate">{right}</span>}
    </div>
  );
}

/** Fila de lista con hairline (§01/§05). */
export function RowL({ children, last = false, className = '' }: {
  children: ReactNode;
  last?: boolean;
  className?: string;
}): JSX.Element {
  return (
    <div
      className={`flex items-start justify-between gap-4 py-[15px] ${last ? '' : 'border-b border-nav-hair'} ${className}`}
    >
      {children}
    </div>
  );
}

/**
 * La capa del spec (§08): modal centrado de 440-460px sobre el caso. Reusa
 * ModalShell (portal, ESC, body-lock contado) y le pone el vestido del brand.
 * Al cerrarla, el caso sigue exactamente donde estaba.
 */
export function NavLayer({ open, onClose, kicker, title, subtitle, children, wide = false }: {
  open: boolean;
  onClose: () => void;
  kicker: string;
  title: string;
  subtitle?: string;
  children: ReactNode;
  wide?: boolean;
}): JSX.Element | null {
  return (
    <ModalShell
      open={open}
      onClose={onClose}
      widthClass={wide ? 'max-w-[560px]' : 'max-w-[460px]'}
      ariaLabelledBy="nav-layer-title"
    >
      <div className="modal-scroll overflow-y-auto font-body text-nav-navy px-6 pt-6 pb-6">
        <Kicker className="mb-1.5">{kicker}</Kicker>
        <h2 id="nav-layer-title" className="font-head text-[19px] font-bold m-0 mb-0.5">{title}</h2>
        {subtitle && <p className="text-[13px] text-nav-slate m-0 mb-4">{subtitle}</p>}
        {children}
      </div>
    </ModalShell>
  );
}

/**
 * Drawer de sólo lectura (§08, «el screening de Wilfredo»): se desliza sobre el
 * caso por la derecha; al cerrarlo seguís donde estabas. ESC cierra; el body
 * queda bloqueado con el lock compartido (a diferencia del KbPanel viejo).
 */
export function NavDrawer({ open, onClose, kicker, subtitle, children }: {
  open: boolean;
  onClose: () => void;
  kicker: string;
  subtitle?: string;
  children: ReactNode;
}): JSX.Element | null {
  useBodyScrollLock(open);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end animate-fade-in" role="dialog" aria-modal="true">
      <button
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        className="flex-1 bg-nav-navy/40 cursor-default"
        onClick={onClose}
      />
      <aside className="w-full max-w-[400px] bg-white h-full flex flex-col shadow-[-14px_0_40px_rgba(10,24,38,.3)] font-body text-nav-navy">
        <div className="px-6 pt-6 pb-2">
          <div className="flex items-start justify-between gap-2.5 mb-1">
            <Kicker className="mb-0">{kicker}</Kicker>
            <button type="button" className={BTN_GHO} onClick={onClose} aria-label="Cerrar">✕</button>
          </div>
          {subtitle && <p className="text-[12.5px] text-nav-slate m-0">{subtitle}</p>}
        </div>
        <div className="modal-scroll flex-1 overflow-y-auto px-6 pb-6">{children}</div>
      </aside>
    </div>
  );
}

/** Aviso destacado con fondo tintado (no dejar mensaje, ALTO, etc.). */
export function TintBox({ tone, children, className = '' }: {
  tone: 'amber' | 'teal' | 'sage' | 'red' | 'grey';
  children: ReactNode;
  className?: string;
}): JSX.Element {
  const tones: Record<string, string> = {
    amber: 'bg-nav-amberTint text-nav-amberText',
    teal: 'bg-nav-tealTint text-nav-tealDark',
    sage: 'bg-nav-sageTint text-nav-sageText',
    red: 'bg-nav-redTint text-nav-red',
    grey: 'bg-nav-soft text-nav-slate border border-nav-card',
  };
  return <div className={`rounded-[9px] px-3.5 py-3 text-[13.5px] ${tones[tone]} ${className}`}>{children}</div>;
}

/**
 * La opción tipo radio grande de las capas (§08): fondo teal-tint cuando está
 * elegida, anillo redondo a la izquierda.
 */
export function ChoiceRow({ on, onPick, children, hint, disabled = false }: {
  on: boolean;
  onPick: () => void;
  children: ReactNode;
  hint?: string | null;
  disabled?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onPick}
      disabled={disabled}
      aria-pressed={on}
      className={`flex items-center gap-[11px] w-full text-left rounded-[9px] px-[13px] py-2.5 font-body text-[14.5px] text-nav-navy cursor-pointer border-[1.5px] transition-colors disabled:opacity-45 ${
        on ? 'bg-nav-tealTint border-nav-teal' : 'bg-white border-nav-card hover:border-nav-line'
      }`}
    >
      <span
        aria-hidden="true"
        className={`w-4 h-4 flex-none rounded-full border-[1.5px] ${
          on
            ? 'border-nav-teal bg-nav-teal shadow-[inset_0_0_0_3px_#fff]'
            : 'border-nav-line bg-transparent'
        }`}
      />
      <span>
        {children}
        {hint && <span className="block text-xs text-nav-slate">{hint}</span>}
      </span>
    </button>
  );
}

/** Chip seleccionable (dominios, canales, motivos): pri cuando está elegido. */
export function PickChip({ on, onPick, children, disabled = false }: {
  on: boolean;
  onPick: () => void;
  children: ReactNode;
  disabled?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onPick}
      disabled={disabled}
      aria-pressed={on}
      className={cn(on ? BTN_PRI : BTN_SEC, 'text-[12.5px] px-3 py-[7px]')}
    >
      {children}
    </button>
  );
}
